import {useMemo} from "react";
import {TypeOf} from "zod";
import {useAccessToken} from "../auth";
import {useClientId} from "../ClientIdProvider";
import {
	BookingSettings,
	CompartmentSettings,
	createApiClientWithBaseConfig,
	get_QueryTenantAuditLog,
	LegalSettings,
	SupportSettings,
	TenantAuditSettings,
	TenantCreation,
	TenantMutation,
} from "./client";
import {get_QueryTenants} from "./client.gen";

export function useTenantApi() {
	const accessToken = useAccessToken();
	const clientId = useClientId();
	return useMemo(() => createTenantApi(clientId, accessToken), [accessToken]);
}

export type TenantQuery = TypeOf<get_QueryTenants["parameters"]>["query"];
export type AuditLogQuery = TypeOf<get_QueryTenantAuditLog["parameters"]>["query"];

function createTenantApi(clientId: string | undefined, accessToken: string | undefined) {
	const {api, withConfig} = createApiClientWithBaseConfig(clientId, accessToken);

	return {
		getTenant: (tenantId: string) =>
			api.get(
				"/api/tenants/{tenantId}",
				withConfig({
					path: {tenantId},
				}),
			),
		getTenantSettings: (tenantId: string) =>
			api.get(
				"/api/tenants/{tenantId}/settings",
				withConfig({
					path: {tenantId},
				}),
			),
		getTenantAuditSettings: (tenantId: string) =>
			api.get(
				"/api/tenants/{tenantId}/settings/audit",
				withConfig({
					path: {tenantId},
				}),
			),
		getTenantBookingSettings: (tenantId: string) =>
			api.get(
				"/api/tenants/{tenantId}/settings/booking",
				withConfig({
					path: {tenantId},
				}),
			),
		getTenantCompartmentSettings: (tenantId: string) =>
			api.get(
				"/api/tenants/{tenantId}/settings/compartment",
				withConfig({
					path: {tenantId},
				}),
			),
		getTenantSupportSettings: (tenantId: string) =>
			api.get(
				"/api/tenants/{tenantId}/settings/support",
				withConfig({
					path: {tenantId},
				}),
			),
		getTenantLegalSettings: (tenantId: string) =>
			api.get(
				"/api/tenants/{tenantId}/settings/legal",
				withConfig({
					path: {tenantId},
				}),
			),
		createTenant: (creation: TenantCreation) =>
			api.post(
				"/api/tenants",
				withConfig({
					body: creation,
				}),
			),
		updateTenant: (tenantId: string, mutation: TenantMutation) =>
			api.put(
				"/api/tenants/{tenantId}",
				withConfig({
					path: {tenantId},
					body: mutation,
				}),
			),
		updateTenantBookingSettings: (tenantId: string, mutation: BookingSettings) =>
			api.put(
				"/api/tenants/{tenantId}/settings/booking",
				withConfig({
					path: {tenantId},
					body: mutation,
				}),
			),
		updateTenantAuditSettings: (tenantId: string, mutation: TenantAuditSettings) =>
			api.put(
				"/api/tenants/{tenantId}/settings/audit",
				withConfig({
					path: {tenantId},
					body: mutation,
				}),
			),
		updateTenantCompartmentSettings: (tenantId: string, mutation: CompartmentSettings) =>
			api.put(
				"/api/tenants/{tenantId}/settings/compartment",
				withConfig({
					path: {tenantId},
					body: mutation,
				}),
			),
		updateTenantSupportSettings: (tenantId: string, mutation: SupportSettings) =>
			api.put(
				"/api/tenants/{tenantId}/settings/support",
				withConfig({
					path: {tenantId},
					body: mutation,
				}),
			),
		updateTenantLegalSettings: (tenantId: string, mutation: LegalSettings) =>
			api.put(
				"/api/tenants/{tenantId}/settings/legal",
				withConfig({
					path: {tenantId},
					body: mutation,
				}),
			),
		deleteTenant: (tenantId: string) =>
			api.delete(
				"/api/tenants/{tenantId}",
				withConfig({
					path: {tenantId},
				}),
			),
		queryTenants: (query: TenantQuery) =>
			api.get(
				"/api/tenants",
				withConfig({
					query,
				}),
			),
		queryTenantAuditLog: (tenantId: string, query: AuditLogQuery) =>
			api.get(
				"/api/tenants/{tenantId}/audit",
				withConfig({
					path: {tenantId},
					query,
				}),
			),
		getTenantPermissions: (tenantId: string) =>
			api.get(
				"/api/tenants/{tenantId}/permissions",
				withConfig({
					path: {tenantId},
				}),
			),
		getTenantFeatures: (tenantId: string) =>
			api.get(
				"/api/tenants/{tenantId}/features",
				withConfig({
					path: {tenantId},
				}),
			),
		getTenantSubscriptions: (tenantId: string) =>
			api.get(
				"/api/tenants/{tenantId}/subscriptions",
				withConfig({
					path: {tenantId},
				}),
			),
	};
}
